import type { InputFrame } from '../../core/input';
import { characterDef, type CharacterId } from '../../content/characters';
import type { EmitterDef } from '../../content/types';
import type { SimCtx } from '../context';
import { EL_FIRE, EL_FROST, EL_LIGHTNING, EL_TEMPORAL, SIDE_ENEMY, SIDE_PLAYER, wrapCoord, wrapDelta, type PlayerState } from '../world';
import { BULLET_VISUALS, emitBurst } from './projectiles';
import { killEnemy } from './enemies';
import { datan2, dcos, dhypot, dsin } from '../../core/dmath';
import { BASE_STATS } from '../stats';

export const DASH_DURATION = 0.22;
export const DASH_BUFFER = 0.12;

const ARCANE_NOVA: EmitterDef = { shape: 'radial', interval: 1, bursts: 1, count: 12, bullet: { speed: 11, lifetime: 1.25, radius: 0.24, damage: 1, visual: 'star', color: 0xb48cff } };
const PIERCING_VOLLEY: EmitterDef = { shape: 'aimed', interval: 1, bursts: 1, count: 5, spread: 0.48, bullet: { speed: 16, lifetime: 1.1, radius: 0.16, damage: 1, visual: 'needle', color: 0x7dff9a } };

/** Exact distance curve: fast launch, smooth deceleration, same total distance. */
function dashEaseOut(progress: number): number {
  const remaining = 1 - progress;
  return 1 - remaining * remaining;
}

/**
 * THE STATIONARY-FIRE RULE
 * ------------------------
 * fireRateMult = 1                       while stationary
 *              = stats.movingFireMult    while moving (default 0 → cannot fire)
 *              = stats.dashFireMult      while dashing (default 0)
 * damageMult   = stats.stillBonusMult    if stillTime < stats.stillBonusWindow
 *              × (1 + min(stillRampMax, stillRamp × stillTime))
 * Upgrades ("rule-breakers") only touch these stats, so the rule stays data-driven.
 */
export function fireRateMultiplier(ctx: SimCtx, p: PlayerState): number {
  const s = ctx.stats[p.index];
  const base = p.dashT > 0 ? s.dashFireMult : p.moving ? s.movingFireMult : 1;
  return base * (ctx.skillFlags[p.index].has('berserk') && p.hp < s.maxHp * 0.4 ? 1.2 : 1);
}

function keystoneDamageMultiplier(ctx: SimCtx, p: PlayerState): number {
  return ctx.skillFlags[p.index].has('berserk') && p.hp < ctx.stats[p.index].maxHp * 0.4 ? 1.3 : 1;
}

export function stillDamageMultiplier(ctx: SimCtx, p: PlayerState): number {
  const s = ctx.stats[p.index];
  let m = 1;
  if (!p.moving && p.dashT <= 0) {
    if (s.stillBonusWindow > 0 && p.stillTime < s.stillBonusWindow) m *= s.stillBonusMult;
    if (s.stillRamp > 0) m *= 1 + Math.min(s.stillRampMax, s.stillRamp * p.stillTime);
  }
  return m;
}

export function updatePlayers(ctx: SimCtx, inputs: InputFrame[], dt: number): void {
  const w = ctx.w;
  const half = w.half;
  for (let i = 0; i < w.players.length; i++) {
    const p = w.players[i];
    p.px = p.x;
    p.py = p.y;
    if (!p.alive) continue;
    const s = ctx.stats[i];
    const inp = inputs[i];
    const character = characterDef(ctx.characters[i]);
    const abilityCharges = character.id === 'knight' ? s.dashCharges : character.ability.charges;
    const abilityCooldown = character.id === 'knight' ? s.dashCooldown : character.ability.cooldown * (s.dashCooldown / BASE_STATS.dashCooldown);

    // timers
    if (p.iframes > 0) p.iframes -= dt;
    if (p.hitFlash > 0) p.hitFlash -= dt;
    if (p.keystoneCooldown > 0) p.keystoneCooldown = Math.max(0, p.keystoneCooldown - dt);
    if (p.keystoneWindow > 0) p.keystoneWindow = Math.max(0, p.keystoneWindow - dt);
    if (p.dashChargesLeft < abilityCharges) {
      p.dashCdT -= dt;
      if (p.dashCdT <= 0) {
        p.dashChargesLeft++;
        p.dashCdT = abilityCooldown;
      }
    }

    // regen
    if (s.regen > 0 && p.hp < s.maxHp) p.hp = Math.min(s.maxHp, p.hp + s.regen * dt);

    // Remember intentional movement separately from `facing`, which auto-aim changes.
    const wantMove = inp.mx !== 0 || inp.my !== 0;
    if (wantMove) {
      const inputLength = dhypot(inp.mx, inp.my) || 1;
      p.moveDx = inp.mx / inputLength;
      p.moveDy = inp.my / inputLength;
    }

    // Buffer presses briefly so inputs just before recharge/dash-end are not lost.
    if (inp.dash) p.dashBufferT = DASH_BUFFER;
    else if (p.dashBufferT > 0) p.dashBufferT = Math.max(0, p.dashBufferT - dt);
    if (p.dashBufferT > 0 && p.dashT <= 0 && p.dashChargesLeft > 0) {
      p.dashChargesLeft--;
      if (p.dashCdT <= 0) p.dashCdT = abilityCooldown;
      p.dashBufferT = 0;
      const dx = wantMove ? inp.mx : p.moveDx;
      const dy = wantMove ? inp.my : p.moveDy;
      const l = dhypot(dx, dy) || 1;
      p.dashDx = dx / l;
      p.dashDy = dy / l;
      activateAbility(ctx, p, character.id);
    }

    // movement
    let vx = 0;
    let vy = 0;
    if (p.dashT > 0) {
      const before = 1 - p.dashT / DASH_DURATION;
      p.dashT = Math.max(0, p.dashT - dt);
      const after = 1 - p.dashT / DASH_DURATION;
      const dashSpeed = s.dashDistance * (dashEaseOut(after) - dashEaseOut(before)) / dt;
      vx = p.dashDx * dashSpeed;
      vy = p.dashDy * dashSpeed;
      p.moving = true;
      p.stillTime = 0;
    } else if (wantMove) {
      vx = inp.mx * s.moveSpeed;
      vy = inp.my * s.moveSpeed;
      p.facing = datan2(vy, vx);
      p.moving = true;
      p.stillTime = 0;
    } else {
      p.moving = false;
      p.stillTime += dt;
    }
    if (ctx.skillFlags[i].has('deadeye') && !p.deadeyeArmed) {
      if (!p.moving && p.dashT <= 0) {
        p.deadeyeCharge += dt;
        if (p.deadeyeCharge >= 1) p.deadeyeArmed = true;
      } else p.deadeyeCharge = 0;
    }
    p.vx = vx;
    p.vy = vy;
    const nx = wrapCoord(p.x + vx * dt, half);
    const ny = wrapCoord(p.y + vy * dt, half);
    // keep px/py continuous with the new position so interpolation never crosses the seam
    if (Math.abs(nx - p.x) > half) p.px = nx - vx * dt;
    if (Math.abs(ny - p.y) > half) p.py = ny - vy * dt;
    p.x = nx;
    p.y = ny;

    // auto-target + fire
    p.fireCd -= dt;
    const rate = fireRateMultiplier(ctx, p);
    if (rate > 0) {
      const target = acquireTarget(ctx, p, s.range);
      p.targetEnemy = target;
      if (target !== 0 && p.fireCd <= 0) {
        fireWeapon(ctx, p, target, 1);
        const weapon = ctx.weapons[i];
        p.fireCd = weapon.interval / (s.fireRate * rate);
        if (s.doubleTap > 0) {
          p.echoLeft = s.doubleTap | 0;
          p.echoT = s.doubleTapDelay;
        }
      }
    } else {
      p.targetEnemy = 0;
      if (p.fireCd < 0) p.fireCd = 0;
    }

    // Double Tap echoes: committed once the first shot leaves, so they land even if the
    // player has started moving again (that's the point of the upgrade).
    if (p.echoLeft > 0) {
      p.echoT -= dt;
      if (p.echoT <= 0) {
        let target = p.targetEnemy;
        const e = ctx.w.enemies;
        if (target === 0 || !e.pool.alive[target]) target = acquireTarget(ctx, p, s.range);
        if (target !== 0) fireWeapon(ctx, p, target, s.doubleTapDamage);
        p.echoLeft--;
        p.echoT = s.doubleTapDelay;
      }
    }

    // temporal rewind pulse: pushes enemy bullets away / reverses them
    if (s.temporalRewind > 0) {
      p.temporalPulseT -= dt;
      if (p.temporalPulseT <= 0) {
        p.temporalPulseT = s.temporalPulseInterval;
        rewindPulse(ctx, p, s.temporalRewind);
      }
    }
  }
}

function activateAbility(ctx: SimCtx, p: PlayerState, character: CharacterId): void {
  const s = ctx.stats[p.index];
  const aim = datan2(p.dashDy, p.dashDx);
  p.facing = aim;
  if (character === 'knight') {
    p.dashT = DASH_DURATION;
    p.iframes = Math.max(p.iframes, s.dashIframes);
    if (ctx.skillFlags[p.index].has('aegis_dash') && p.keystoneCooldown <= 0) {
      p.keystoneShield = true;
      p.keystoneCooldown = 4;
    }
    ctx.events.push({ type: 'dash', player: p.index, x: p.x, y: p.y, dx: p.dashDx, dy: p.dashDy });
    return;
  }

  if (character === 'barbarian') {
    const e = ctx.w.enemies;
    for (let j = 1; j < e.pool.high; j++) {
      if (!e.pool.alive[j] || e.invuln[j] > 0) continue;
      const dx = wrapDelta(e.x[j] - p.x, ctx.w.half);
      const dy = wrapDelta(e.y[j] - p.y, ctx.w.half);
      const d2 = dx * dx + dy * dy;
      if (d2 > 20.25) continue;
      const damage = s.damage * s.damageMult * 2 * keystoneDamageMultiplier(ctx, p);
      let hpDamage = damage;
      if (e.shield[j] > 0) {
        const absorbed = Math.min(e.shield[j], hpDamage);
        e.shield[j] -= absorbed;
        hpDamage -= absorbed;
        if (e.shield[j] <= 0) ctx.events.push({ type: 'sfx', id: 'shieldBreak', x: e.x[j], y: e.y[j] });
      }
      e.hp[j] -= hpDamage;
      e.hitFlash[j] = 0.12;
      const length = Math.sqrt(d2) || 1;
      if (!e.isBoss[j]) {
        e.x[j] = wrapCoord(e.x[j] + (dx / length) * 1.6, ctx.w.half);
        e.y[j] = wrapCoord(e.y[j] + (dy / length) * 1.6, ctx.w.half);
      }
      p.damageDealt += hpDamage;
      ctx.events.push({ type: 'hit', x: e.x[j], y: e.y[j], dmg: damage, crit: false, side: SIDE_PLAYER });
      if (e.hp[j] <= 0) killEnemy(ctx, j, p.index);
    }
    ctx.events.push({ type: 'ability', player: p.index, ability: 'warCry', x: p.x, y: p.y, dx: p.dashDx, dy: p.dashDy });
    return;
  }

  if (character === 'rogue') {
    p.x = wrapCoord(p.x + p.dashDx * 4.5, ctx.w.half);
    p.y = wrapCoord(p.y + p.dashDy * 4.5, ctx.w.half);
    p.px = p.x;
    p.py = p.y;
    p.iframes = Math.max(p.iframes, 0.3);
    if (ctx.skillFlags[p.index].has('phantom')) p.keystoneWindow = 0.6;
    const shots = ctx.w.projectiles;
    for (let j = 1; j < shots.pool.high; j++) {
      if (!shots.pool.alive[j] || shots.side[j] !== SIDE_ENEMY) continue;
      const dx = wrapDelta(shots.x[j] - p.x, ctx.w.half);
      const dy = wrapDelta(shots.y[j] - p.y, ctx.w.half);
      if (dx * dx + dy * dy <= 9) shots.pool.release(j);
    }
    ctx.events.push({ type: 'ability', player: p.index, ability: 'phaseStep', x: p.x, y: p.y, dx: p.dashDx, dy: p.dashDy });
    return;
  }

  let shotAngle = aim;
  if (character === 'ranger') {
    const target = acquireTarget(ctx, p, s.range * 1.5);
    if (target !== 0) shotAngle = datan2(wrapDelta(ctx.w.enemies.y[target] - p.y, ctx.w.half), wrapDelta(ctx.w.enemies.x[target] - p.x, ctx.w.half));
  }
  const burst = ctx._burst;
  Object.assign(burst, { side: SIDE_PLAYER, owner: p.index, damage: s.damage * s.damageMult * (character === 'mage' ? 1.25 : 1.8) * keystoneDamageMultiplier(ctx, p), pierce: character === 'mage' ? 2 : 4, bounce: 0, elements: character === 'mage' ? EL_TEMPORAL : 0, critChance: s.critChance, critMult: s.critMult, speedMult: s.projectileSpeed, radiusMult: s.projectileSize, stillBonus: 1 });
  emitBurst(burst, character === 'mage' ? ARCANE_NOVA : PIERCING_VOLLEY, p.x, p.y, shotAngle, p.shotIndex++);
  if (character === 'mage' && ctx.skillFlags[p.index].has('overload')) {
    p.keystoneCounter = (p.keystoneCounter + 1) % 4;
    if (p.keystoneCounter === 0) emitBurst(burst, ARCANE_NOVA, p.x, p.y, shotAngle + Math.PI / 12, p.shotIndex++);
  }
  ctx.events.push({ type: 'ability', player: p.index, ability: character === 'mage' ? 'arcaneNova' : 'piercingVolley', x: p.x, y: p.y, dx: dcos(shotAngle), dy: dsin(shotAngle) });
}

/** Nearest living enemy within range; bosses get priority when within 60% of range. Returns slot or 0. */
function acquireTarget(ctx: SimCtx, p: PlayerState, range: number): number {
  const e = ctx.w.enemies;
  const r2 = range * range;
  let best = r2;
  let bestSlot = 0;
  const high = e.pool.high;
  const half = ctx.w.half;
  for (let j = 1; j < high; j++) {
    if (!e.pool.alive[j] || Math.abs(e.z[j]) > 0.5) continue;
    const dx = wrapDelta(e.x[j] - p.x, half);
    const dy = wrapDelta(e.y[j] - p.y, half);
    let d2 = dx * dx + dy * dy;
    if (e.isBoss[j]) d2 *= 0.36; // priority
    if (d2 < best) {
      best = d2;
      bestSlot = j;
    }
  }
  return bestSlot;
}

function fireWeapon(ctx: SimCtx, p: PlayerState, target: number, damageScale: number): void {
  const s = ctx.stats[p.index];
  const weapon = ctx.weapons[p.index];
  const e = ctx.w.enemies;
  // lead the target slightly for fast projectiles
  const dx = wrapDelta(e.x[target] - p.x, ctx.w.half);
  const dy = wrapDelta(e.y[target] - p.y, ctx.w.half);
  const dist = dhypot(dx, dy) || 1;
  const bulletSpeed = weapon.bullet.speed * s.projectileSpeed;
  const lead = Math.min(0.4, dist / bulletSpeed) * 0.6;
  const aim = datan2(dy + e.vy[target] * lead, dx + e.vx[target] * lead);
  p.facing = aim;

  let elements = 0;
  if (s.fireChance > 0) elements |= EL_FIRE;
  if (s.frostChance > 0) elements |= EL_FROST;
  if (s.lightningChance > 0) elements |= EL_LIGHTNING;
  if (s.temporalChance > 0) elements |= EL_TEMPORAL;

  const b = ctx._burst;
  b.side = SIDE_PLAYER;
  b.owner = p.index;
  b.damage = s.damage * s.damageMult * damageScale * keystoneDamageMultiplier(ctx, p);
  b.pierce = s.pierce;
  b.bounce = s.bounce;
  b.elements = elements;
  b.critChance = p.deadeyeArmed ? 1 : s.critChance;
  b.critMult = s.critMult;
  b.speedMult = s.projectileSpeed;
  b.radiusMult = s.projectileSize;
  b.stillBonus = stillDamageMultiplier(ctx, p);

  const em = ctx._weaponEmitter;
  em.shape = weapon.shape;
  em.count = weapon.count + s.projectiles;
  em.spread = (weapon.spread ?? 0.35) * (1 + s.projectiles * 0.12);
  em.rotatePerBurst = weapon.rotatePerBurst;
  em.bullet = weapon.bullet;
  em.aim = weapon.shape === 'aimed';
  p.shotIndex = (p.shotIndex + 1) | 0;
  emitBurst(b, em, p.x + dcos(aim) * 0.5, p.y + dsin(aim) * 0.5, aim, p.shotIndex);
  if (p.deadeyeArmed) {
    p.deadeyeArmed = false;
    p.deadeyeCharge = 0;
  }
  ctx.events.push({
    type: 'sfx', id: 'shoot', x: p.x, y: p.y,
    projectile: { color: weapon.bullet.color, visual: BULLET_VISUALS.indexOf(weapon.bullet.visual), elements },
  });

  // restore defaults for enemy usage
  b.side = 1;
  b.owner = 0;
  b.pierce = 0;
  b.bounce = 0;
  b.elements = 0;
  b.critChance = 0;
  b.critMult = 1;
  b.speedMult = 1;
  b.radiusMult = 1;
  b.stillBonus = 1;
}

function rewindPulse(ctx: SimCtx, p: PlayerState, radius: number): void {
  const pr = ctx.w.projectiles;
  const r2 = radius * radius;
  for (let i = 1; i < pr.pool.high; i++) {
    if (!pr.pool.alive[i] || pr.side[i] !== 1) continue;
    const dx = wrapDelta(pr.x[i] - p.x, ctx.w.half);
    const dy = wrapDelta(pr.y[i] - p.y, ctx.w.half);
    if (dx * dx + dy * dy < r2) {
      pr.vx[i] = -pr.vx[i];
      pr.vy[i] = -pr.vy[i];
      pr.homing[i] = 0;
      pr.side[i] = 0; // rewound bullets become the player's
      pr.owner[i] = p.index;
      pr.damage[i] = ctx.stats[p.index].damage * ctx.stats[p.index].damageMult * 0.5;
      pr.pierce[i] = 1;
    }
  }
  ctx.events.push({ type: 'sfx', id: 'rewind', x: p.x, y: p.y });
  ctx.events.push({ type: 'shake', strength: 0.3 });
}
